<template>
  <AppLayout>
    <div class="transaction fleet-module-page">
      <section class="container">
        <div class="fleet-stat-grid">
          <div class="fleet-stat-card"><span>Total Records</span><strong>{{ complianceStats?.total || 0 }}</strong></div>
          <div class="fleet-stat-card"><span>Active</span><strong>{{ complianceStats?.active || 0 }}</strong></div>
          <div class="fleet-stat-card"><span>Expiring Soon</span><strong>{{ complianceStats?.expiring || 0 }}</strong></div>
          <div class="fleet-stat-card"><span>Expired</span><strong>{{ complianceStats?.expired || 0 }}</strong></div>
        </div>

        <div class="filter-search">
          <div class="filter-search-header"><p>Filter</p></div>
          <div class="filter-search-content">
            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label>Search</label>
                  <input v-model="filters.search" class="form-control" placeholder="Vehicle or type..." />
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>Vehicle</label>
                  <select v-model="filters.vehicleId" class="form-control">
                    <option value="">All Vehicles</option>
                    <option v-for="v in vehicles" :key="v.id" :value="String(v.id)">{{ v.plateNumber }}</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>Status</label>
                  <select v-model="filters.status" class="form-control">
                    <option value="">All</option>
                    <option value="ACTIVE">Active</option>
                    <option value="EXPIRING">Expiring Soon</option>
                    <option value="EXPIRED">Expired</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="table-toolbar">
          <button class="btn btn-style-2 btn-sm" @click="openCreateModal">Add Compliance</button>
        </div>

        <div class="data-table-wrapper">
          
          <div class="table-wrap">
            <table ref="fleetTable" class="fleet-table table table-hover">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Vehicle</th>
                  <th>Type</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, index) in filteredCompliances" :key="c.id || c.compliance_id">
                  <td>{{ index + 1 }}</td>
                  <td>{{ c?.vehicle?.plateNumber || '-' }}</td>
                  <td>{{ c?.type?.name || '-' }}</td>
                  <td>{{ formatDate(c.expiryDate) }}</td>
                  <td><span class="status-pill" :class="statusClass(getComplianceState(c))">{{ getComplianceState(c) }}</span></td>
                  <td>
                    <div class="table-actions">
                      <RouterLink class="mini-btn" :to="`/fleet/compliance/${c.id || c.compliance_id}`">View</RouterLink>
                      <button class="mini-btn" @click="startEdit(c)">Edit</button>
                      <button class="mini-btn" @click="sendTest(c)">Test Email</button>
                      <button class="mini-btn danger" @click="removeCompliance(c)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="showModal" class="svc-modal-overlay" @click.self="closeModal">
          <div class="svc-modal-box">
            <div class="svc-modal-header">
              <h6 class="fw-bold mb-0">{{ editId ? 'Edit Compliance Record' : 'Add Compliance Record' }}</h6>
              <button class="btn-close-plain" @click="closeModal">&times;</button>
            </div>
            <div class="svc-modal-body">
              <div class="row g-2">
                <div class="col-md-6">
                  <label class="form-label" style="font-size:11px;">Vehicle</label>
                  <select v-model.number="form.vehicle_id" class="form-control form-control-sm">
                    <option :value="null">Select vehicle</option>
                    <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.plateNumber }}</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label" style="font-size:11px;">Compliance Type</label>
                  <select v-model.number="form.compliance_type_id" class="form-control form-control-sm">
                    <option :value="null">Select type</option>
                    <option v-for="t in complianceTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label" style="font-size:11px;">Expiry Date</label>
                  <input v-model="form.expiryDate" type="date" class="form-control form-control-sm" />
                </div>
              </div>
              <p v-if="errorMessage" class="text-danger small mt-2">{{ errorMessage }}</p>
            </div>
            <div class="svc-modal-footer">
              <button class="btn btn-sm btn-style-2" @click="closeModal">Cancel</button>
              <button class="btn btn-sm btn-primary" @click="saveCompliance" :disabled="saving">
                <span v-if="saving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                <span v-else>Save Record</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import fleetApi from '@/api/fleet';
import Swal from '@/utils/swal';
import 'datatables.net-vue3';
import 'datatables.net-bs5';
import 'datatables.net-buttons-bs5';
import 'datatables.net-responsive-bs5';
import $ from 'jquery';
import 'jszip';
import 'datatables.net-buttons/js/buttons.html5.js';
import 'datatables.net-buttons/js/buttons.colVis.js';
import 'datatables.net-buttons/js/buttons.print.js';

function emptyForm() {
  return {
    vehicle_id: null,
    compliance_type_id: null,
    expiryDate: '',
  };
}

export default {
  name: 'FleetCompliance',
  components: { AppLayout },
  data() {
    return {
      compliances: [],
      vehicles: [],
      complianceTypes: [],
      dataTable: null,
      showModal: false,
      editId: null,
      saving: false,
      errorMessage: '',
      filters: {
        search: '',
        vehicleId: '',
        status: '',
      },
      form: emptyForm(),
    };
  },
  computed: {
    filteredCompliances() {
      const q = String(this.filters.search || '').toLowerCase().trim();
      return (this.compliances || []).filter((c) => {
        const text = [c?.vehicle?.plateNumber, c?.type?.name, c.status].filter(Boolean).join(' ').toLowerCase();
        const matchesSearch = !q || text.includes(q);
        const matchesVehicle = !this.filters.vehicleId || String(c?.vehicle?.id || c.vehicle_id || c.vehicleId) === String(this.filters.vehicleId);
        const matchesStatus = !this.filters.status || this.getComplianceState(c) === this.filters.status;
        return matchesSearch && matchesVehicle && matchesStatus;
      });
    },
    complianceStats() {
      const items = Array.isArray(this.compliances) ? this.compliances : [];
      const active = items.filter((c) => this.getComplianceState(c) === 'ACTIVE').length;
      const expiring = items.filter((c) => this.getComplianceState(c) === 'EXPIRING').length;
      const expired = items.filter((c) => this.getComplianceState(c) === 'EXPIRED').length;
      return { total: items.length, active, expiring, expired };
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.initializeDataTable();
      },
    },
  },
  methods: {
    destroyDataTable() {
      if (this.dataTable && typeof this.dataTable.destroy === 'function') {
        this.dataTable.destroy();
        this.dataTable = null;
      }
    },
    initializeDataTable() {
      this.$nextTick(() => {
        const tableElement = this.$refs.fleetTable;
        if (!tableElement) return;
        this.destroyDataTable();
        this.dataTable = $(tableElement).DataTable({
          responsive: true,
          destroy: true,
          pageLength: 10,
          lengthMenu: [[10, 25, 50, 100, -1], [10, 25, 50, 100, 'All']],
          language: {
            emptyTable: 'No compliance records found',
            paginate: {
              previous: '<i class="fa fa-chevron-left"></i>',
              next: '<i class="fa fa-chevron-right"></i>',
            },
          },
          dom: 'Bfrtip',
          buttons: [
            'colvis',
            'pageLength',
            {
              extend: 'csv',
              text: 'Export CSV',
              className: 'btn btn-sm btn-outline-primary',
              exportOptions: { columns: ':visible' },
            },
          ],
        });
      });
    },
    notifyError(err) {
      this.$toast?.error?.(err?.message || 'Request failed');
    },
    formatDate(v) {
      if (!v) return '-';
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
    },
    getComplianceState(record) {
      const explicit = String(record?.status || '').toUpperCase();
      if (explicit.includes('EXPIRED')) return 'EXPIRED';
      if (explicit.includes('EXPIRING')) return 'EXPIRING';
      if (!record?.expiryDate) return 'ACTIVE';
      const expiry = new Date(record.expiryDate).getTime();
      const diffDays = Math.ceil((expiry - Date.now()) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'EXPIRED';
      if (diffDays <= 30) return 'EXPIRING';
      return 'ACTIVE';
    },
    statusClass(status) {
      if (status === 'ACTIVE') return 'status-active';
      if (status === 'EXPIRING') return 'status-expiring';
      if (status === 'EXPIRED') return 'status-expired';
      return 'status-default';
    },
    async loadData() {
      try {
        const [complianceRes, vehiclesRes, typesRes] = await Promise.all([
          fleetApi.listCompliances({ page: 1, limit: 200 }),
          fleetApi.listVehicles({ page: 1, limit: 200 }),
          fleetApi.listComplianceTypes(),
        ]);
        this.compliances = fleetApi.unwrapList(complianceRes);
        this.vehicles = fleetApi.unwrapList(vehiclesRes);
        this.complianceTypes = fleetApi.unwrapList(typesRes);
        this.initializeDataTable();
      } catch (err) {
        this.notifyError(err);
      }
    },
    openCreateModal() {
      this.editId = null;
      this.form = emptyForm();
      this.errorMessage = '';
      this.showModal = true;
    },
    startEdit(c) {
      this.editId = c.id || c.compliance_id;
      this.form = {
        vehicle_id: c.vehicle_id || c.vehicleId || c?.vehicle?.id || null,
        compliance_type_id: c.compliance_type_id || c?.type?.id || null,
        expiryDate: c.expiryDate ? String(c.expiryDate).slice(0, 10) : '',
      };
      this.errorMessage = '';
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editId = null;
      this.form = emptyForm();
      this.errorMessage = '';
    },
    async saveCompliance() {
      if (!this.form.vehicle_id || !this.form.compliance_type_id || !this.form.expiryDate) {
        this.errorMessage = 'Vehicle, compliance type and expiry date are required.';
        return;
      }

      this.saving = true;
      try {
        if (this.editId) {
          await fleetApi.updateCompliance(this.editId, this.form);
          this.$toast?.success?.('Compliance record updated');
        } else {
          await fleetApi.createCompliance(this.form);
          this.$toast?.success?.('Compliance record created');
        }
        this.closeModal();
        await this.loadData();
      } catch (err) {
        this.notifyError(err);
      } finally {
        this.saving = false;
      }
    },
    async sendTest(c) {
      const id = c.id || c.compliance_id;
      if (!id) return;
      try {
        await fleetApi.testComplianceEmail(id);
        this.$toast?.success?.('Test email sent');
      } catch (err) {
        this.notifyError(err);
      }
    },
    async removeCompliance(c) {
      const id = c.id || c.compliance_id;
      if (!id) return;
      const result = await Swal.fire({
        title: 'Delete this compliance record?',
        text: 'This action cannot be undone!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;
      try {
        await fleetApi.deleteCompliance(id);
        this.$toast?.success?.('Compliance record deleted');
        await this.loadData();
      } catch (err) {
        this.notifyError(err);
      }
    },
  },
  mounted() {
    this.loadData();
  },
  beforeUnmount() {
    this.destroyDataTable();
  },
};
</script>

<style scoped>
@import './fleet.shared.css';
</style>
